/**
 * AdjustmentsSheet — the scorer's "Adjust" key: give or take away runs or
 * points, with a reason. The event's umpire penalties from the rulebook appear
 * as one-tap presets. Runs change the team's innings total (not player stats);
 * points count in the league table. Every adjustment can be undone.
 * In a match, the presets are that match's rules (its rulebook version, for
 * its stage — `umpireButtons`): a per-player penalty asks how many players,
 * and a "warn first" rule says so.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import RuleSheet from './RuleSheet';
import { adjustmentsApi, rulebookApi, errorText } from './api';

const chip = (on) => `px-3 py-2 rounded-full text-sm font-semibold border ${on ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'bg-white text-[#2B3A67] border-gray-200'}`;

export default function AdjustmentsSheet({ open, onClose, eventId, fixture, teams, onChanged, umpireButtons, battingTeamId }) {
  const matchTeams = (teams || []).filter((t) => [fixture?.team_a_id, fixture?.team_b_id].includes(t.id));
  const [list, setList] = useState([]);
  const [presets, setPresets] = useState([]);
  const [teamId, setTeamId] = useState(null);
  const [kind, setKind] = useState('runs');
  const [takeAway, setTakeAway] = useState(true);
  const [amount, setAmount] = useState(5);
  const [reason, setReason] = useState('');
  const [ruleKey, setRuleKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preset, setPreset] = useState(null);
  const [count, setCount] = useState(1);

  useEffect(() => {
    if (!open || !fixture) return;
    adjustmentsApi.list(eventId, fixture.id).then(setList).catch(() => setList([]));
    if (umpireButtons) {
      setPresets(umpireButtons.filter((b) => b.params?.penalty_runs)
        .map((b) => ({ id: b.rule_key, rule_key: b.rule_key, params: b.params, label: b.block === 'innings_time_limit' ? 'Innings over time' : b.sentence })));
    } else {
      rulebookApi.get(eventId).then((rb) => {
        setPresets((rb.rules || []).filter((r) => r.kind === 'umpire' && r.params?.penalty_runs));
      }).catch(() => setPresets([]));
    }
    if (battingTeamId) setTeamId((t) => t || battingTeamId);
  }, [open, eventId, fixture, umpireButtons, battingTeamId]);

  const teamName = (id) => matchTeams.find((t) => t.id === id)?.team_name || 'Team';
  const applyPreset = (r, n = 1) => {
    setKind('runs');
    setTakeAway(true);
    setPreset(r);
    setCount(n);
    setAmount(r.params.penalty_runs * n);
    const what = r.params.offence || r.label;
    setReason(r.params.per === 'player' && n > 1 ? `${what} (${n} players)` : what);
    setRuleKey(r.rule_key);
  };

  const save = async () => {
    if (!teamId) return toast.error('Choose the team');
    if (!reason.trim()) return toast.error('Add a reason');
    setBusy(true);
    try {
      const adj = await adjustmentsApi.add(eventId, {
        fixture_id: fixture.id, team_id: teamId, kind, amount: takeAway ? -Math.abs(amount) : Math.abs(amount),
        reason: reason.trim(), rule_key: ruleKey,
      });
      setList((l) => [...l, adj]);
      toast.success(`${adj.amount > 0 ? '+' : ''}${adj.amount} ${kind} · ${adj.team_name}`);
      setReason('');
      setRuleKey(null);
      setPreset(null);
      onChanged?.();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const undo = async (adj) => {
    try {
      await adjustmentsApi.remove(eventId, adj.id);
      setList((l) => l.filter((x) => x.id !== adj.id));
      toast.success('Adjustment undone');
      onChanged?.();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <RuleSheet open={open} onClose={onClose} title={presets.length ? 'Penalty or adjustment' : 'Adjust runs or points'} subtitle="Applied with a reason, shown on the scorecard" testId="adjust-sheet"
      footer={(
        <button onClick={save} disabled={busy} className="w-full h-12 rounded-xl bg-[#2B3A67] text-white font-bold disabled:opacity-50" data-testid="adjust-save">
          {busy ? 'Saving…' : `${takeAway ? 'Take away' : 'Give'} ${amount} ${kind === 'runs' ? (amount === 1 ? 'run' : 'runs') : (amount === 1 ? 'point' : 'points')}`}
        </button>
      )}>
      {presets.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 mb-1.5">From the rulebook</p>
          <div className="flex flex-wrap gap-2">
            {presets.map((r) => (
              <button key={r.id} onClick={() => applyPreset(r)} data-testid="penalty-preset"
                className={`px-3 py-2 rounded-full text-sm font-semibold text-left ${preset?.id === r.id ? 'bg-[#B3263F] text-white' : 'bg-[#FCE8EC] text-[#B3263F]'}`}>
                <i className="ph-bold ph-gavel" /> {r.params.offence || r.label} · −{r.params.penalty_runs}{r.params.per === 'player' ? ' a player' : ''}
              </button>
            ))}
          </div>
          {preset?.params?.warnings_first > 0 && (
            <p className="mt-2 text-xs text-[#2B3A67] bg-[#FEF8DC] rounded-lg px-2 py-1.5">
              The rulebook gives {preset.params.warnings_first} warning{preset.params.warnings_first > 1 ? 's' : ''} first. Apply the penalty only after that.
            </p>
          )}
          {preset?.params?.per === 'player' && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <span className="text-xs font-bold text-gray-500">Players</span>
              <button className="w-9 h-9 rounded-xl bg-[#E9EBF1] font-bold" onClick={() => applyPreset(preset, Math.max(1, count - 1))} aria-label="Fewer players">−</button>
              <span className="w-8 text-center font-bold" data-testid="penalty-players">{count}</span>
              <button className="w-9 h-9 rounded-xl bg-[#E9EBF1] font-bold" onClick={() => applyPreset(preset, Math.min(11, count + 1))} aria-label="More players">+</button>
            </div>
          )}
        </div>
      )}
      <p className="text-xs font-bold text-gray-500 mb-1.5">Team</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {matchTeams.map((t) => (
          <button key={t.id} className={chip(teamId === t.id)} onClick={() => setTeamId(t.id)} data-testid="adjust-team">{t.team_name}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <p className="text-xs font-bold text-gray-500 mb-1.5">What</p>
          <div className="flex gap-2">
            <button className={chip(kind === 'runs')} onClick={() => setKind('runs')}>Runs</button>
            <button className={chip(kind === 'points')} onClick={() => setKind('points')}>Points</button>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold text-gray-500 mb-1.5">Change</p>
          <div className="flex gap-2">
            <button className={chip(takeAway)} onClick={() => setTakeAway(true)}>Take away</button>
            <button className={chip(!takeAway)} onClick={() => setTakeAway(false)}>Give</button>
          </div>
        </div>
      </div>
      <p className="text-xs font-bold text-gray-500 mb-1.5">How many</p>
      <div className="flex items-center gap-2 mb-4">
        <button className="w-11 h-11 rounded-xl bg-[#E9EBF1] text-xl font-bold" onClick={() => setAmount((a) => Math.max(1, a - 1))} aria-label="Fewer">−</button>
        <input inputMode="numeric" value={amount} onChange={(e) => setAmount(Math.min(100, Math.max(1, Number(e.target.value) || 1)))}
          className="w-20 h-11 text-center rounded-xl border border-gray-200 text-lg font-bold" data-testid="adjust-amount" />
        <button className="w-11 h-11 rounded-xl bg-[#E9EBF1] text-xl font-bold" onClick={() => setAmount((a) => Math.min(100, a + 1))} aria-label="More">+</button>
      </div>
      <p className="text-xs font-bold text-gray-500 mb-1.5">Reason</p>
      <input value={reason} onChange={(e) => { setReason(e.target.value); setRuleKey(null); }} maxLength={200}
        placeholder="e.g. Slow over rate" className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm" data-testid="adjust-reason" />

      {list.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-bold text-gray-500 mb-1.5">In this match</p>
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
            {list.map((a) => (
              <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid="adjust-item">
                <span className={`font-extrabold ${a.amount < 0 ? 'text-[#B3263F]' : 'text-[#1F7A75]'}`}>
                  {a.amount > 0 ? '+' : ''}{a.amount} {a.kind === 'runs' ? 'r' : 'pts'}
                </span>
                <span className="flex-1 min-w-0 truncate">{teamName(a.team_id)} · {a.reason}</span>
                <button onClick={() => undo(a)} className="text-xs font-semibold text-gray-500 underline">Undo</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </RuleSheet>
  );
}
